import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { mdy, mdyDow } from "@/lib/dateFmt";
import { Repeat2, ArrowLeftRight, Calendar } from "lucide-react";

export default function TradeRequestDialog({ open, onClose, facilityId, currentUserId, onSubmitted }) {
  const [myShifts, setMyShifts] = useState([]);       // requester's upcoming assignments
  const [candidates, setCandidates] = useState([]);   // facility peers
  const [partnerShifts, setPartnerShifts] = useState([]);
  const [mine, setMine] = useState("");
  const [partnerId, setPartnerId] = useState("");
  const [theirs, setTheirs] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !facilityId) return;
    setMine(""); setPartnerId(""); setTheirs(""); setReason("");
    (async () => {
      try {
        const today = new Date().toISOString().slice(0, 10);
        const [emp, mySched] = await Promise.all([
          api.get(`/employees?facility_id=${facilityId}`),
          api.get(`/my-schedule`),
        ]);
        setCandidates(emp.data.filter((e) => e.id !== currentUserId));
        // Only upcoming, facility-matched shifts
        const upcoming = (mySched.data || [])
          .filter((a) => a.facility_id === facilityId && a.date >= today && a.shift_code)
          .sort((a, b) => a.date.localeCompare(b.date));
        setMyShifts(upcoming);
      } catch { setCandidates([]); setMyShifts([]); }
    })();
  }, [open, facilityId, currentUserId]);

  useEffect(() => {
    if (!open || !partnerId || !facilityId) return;
    (async () => {
      try {
        const today = new Date().toISOString().slice(0, 10);
        // Load all pay periods and their assignments? Simpler: use a partner-shifts endpoint.
        const { data } = await api.get(`/partner-shifts?facility_id=${facilityId}&employee_id=${partnerId}`);
        setPartnerShifts((data || []).filter((a) => a.date >= today).sort((a, b) => a.date.localeCompare(b.date)));
      } catch { setPartnerShifts([]); }
    })();
  }, [open, partnerId, facilityId]);

  const partner = useMemo(() => candidates.find((c) => c.id === partnerId), [candidates, partnerId]);
  const mineA = useMemo(() => myShifts.find((a) => a.date === mine), [myShifts, mine]);
  const theirsA = useMemo(() => partnerShifts.find((a) => a.date === theirs), [partnerShifts, theirs]);

  const submit = async () => {
    if (!partnerId || !mine || !theirs) return;
    setSaving(true);
    try {
      await api.post("/trade-requests", {
        partner_id: partnerId,
        requester_date: mine,
        partner_date: theirs,
        facility_id: facilityId,
        reason: reason || null,
      });
      toast.success("Trade request submitted — awaiting manager decision");
      onSubmitted && onSubmitted();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to submit");
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto thin-scroll" data-testid="trade-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Repeat2 size={18} className="text-blue-600" /> Request a shift trade
          </DialogTitle>
          <DialogDescription>Swap one of your upcoming shifts with a coworker&apos;s.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500">Your shift to give up</Label>
            {myShifts.length === 0 ? (
              <div className="mt-1.5 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500">
                You have no upcoming shifts on this facility.
              </div>
            ) : (
              <select
                className="mt-1.5 w-full rounded-md border border-slate-300 px-2 py-2 text-sm"
                value={mine} onChange={(e) => setMine(e.target.value)}
                data-testid="trade-mine"
              >
                <option value="">— pick a date —</option>
                {myShifts.map((a) => (
                  <option key={a.date} value={a.date}>
                    {mdyDow(a.date)} · {a.shift_code} ({a.hours}h)
                  </option>
                ))}
              </select>
            )}
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500">Trade with</Label>
            <select
              className="mt-1.5 w-full rounded-md border border-slate-300 px-2 py-2 text-sm"
              value={partnerId} onChange={(e) => setPartnerId(e.target.value)}
              data-testid="trade-partner"
            >
              <option value="">— pick a coworker —</option>
              {candidates.map((c) => (
                <option key={c.id} value={c.id}>{c.name} · {c.role_label || c.position || c.role}</option>
              ))}
            </select>
          </div>

          {partnerId && (
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">Their shift to take</Label>
              {partnerShifts.length === 0 ? (
                <div className="mt-1.5 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500">
                  {partner?.name} has no upcoming shifts.
                </div>
              ) : (
                <select
                  className="mt-1.5 w-full rounded-md border border-slate-300 px-2 py-2 text-sm"
                  value={theirs} onChange={(e) => setTheirs(e.target.value)}
                  data-testid="trade-theirs"
                >
                  <option value="">— pick a date —</option>
                  {partnerShifts.map((a) => (
                    <option key={a.date} value={a.date}>
                      {mdyDow(a.date)} · {a.shift_code} ({a.hours}h)
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          {mineA && theirsA && (
            <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900" data-testid="trade-summary">
              <div className="flex items-center gap-2 justify-center text-center">
                <div className="flex-1">
                  <div className="font-semibold">You</div>
                  <div className="text-xs">{mdy(mineA.date)} · {mineA.shift_code} ({mineA.hours}h)</div>
                </div>
                <ArrowLeftRight size={16} className="text-blue-600 shrink-0" />
                <div className="flex-1">
                  <div className="font-semibold">{partner?.name}</div>
                  <div className="text-xs">{mdy(theirsA.date)} · {theirsA.shift_code} ({theirsA.hours}h)</div>
                </div>
              </div>
            </div>
          )}

          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500">Reason (optional)</Label>
            <Textarea
              value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="Why you're proposing this trade…"
              data-testid="trade-reason"
              className="mt-1.5 min-h-[70px]"
            />
          </div>
        </div>

        <DialogFooter className="sm:justify-end pt-2 gap-2">
          <Button variant="outline" onClick={onClose} data-testid="trade-cancel">Cancel</Button>
          <Button
            className="bg-blue-600 hover:bg-blue-700"
            onClick={submit}
            disabled={saving || !partnerId || !mine || !theirs}
            data-testid="trade-submit"
          >
            <Calendar size={14} className="mr-1" /> Submit trade
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
